import React from 'react'

import { LockClosedIcon } from '@heroicons/react/24/outline'
import { Field, Form, Formik } from 'formik'

import DSCRCalculatorField from '@/components/DSCRCalculator'
import { AlertFlag } from '@/features/AlertFlag'
import { useForms } from '@/utils/useForms'

export default function AssetManagementOverridesForm({
  initialValues,
  onSubmit,
  useERA,
  overrides,
  adjustmentOptions,
  allowOverrides,
  baseExchangeRate,
  showLimitTooltip,
  ERLimit,
  exceedLimit,
  applicationOffer,
  rateFormat,
  shouldHideDTI,
  isInvestmentProperty,
  application,
  dscrEditAllowed,
  loggedUser,
}) {
  const {
    setNumericValue,
    defaultNumericInputProps,
    defaultCurrencyInputProps,
    defaultInputClasses,
  } = useForms()

  return (
    <section
      className='group card relative h-full border bg-base-100 shadow has-[.is-dirty]:border-warning'
      id='assets'
      data-test='asset-management-overrides-section'
    >
      <span className='badge badge-warning mx-auto absolute left-1/2 -translate-x-1/2 -translate-y-1/2 hidden group-has-[.is-dirty]:block'>
        Unsaved changes
      </span>
      <div className='card-body'>
        <h4 className='card-title'>Offer Overrides</h4>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          onSubmit={onSubmit}
        >
          {({ isSubmitting, setFieldValue, dirty, values, submitForm }) => (
            // To toggle the [unsaved changes] badge, eval the dirty prop in Formik
            <Form className={dirty ? 'is-dirty' : ''}>
              <fieldset
                className='grid gap-3 gap-x-10 grid-cols-2'
                disabled={isSubmitting}
              >
                {/* Enable once the business rules for locking this section are defined */}
                <span className='absolute right-5 top-5 hidden'>
                  <div
                    className='tooltip hover:tooltip-open tooltip-left tooltip-warning'
                    data-tip='You do not have permission to edit this information'
                  >
                    <LockClosedIcon
                      height={24}
                      width={24}
                    />
                  </div>
                </span>

                {/* THF */}
                <label className='form-control'>
                  <div className='label'>
                    <span className='label-text'>Total Home Financing (THF)</span>
                  </div>
                  <Field
                    name='total_home_financing_limit'
                    {...defaultCurrencyInputProps}
                    placeholder='%'
                    suffix='%'
                    onValueChange={({ floatValue }) =>
                      setNumericValue({
                        setFieldValue,
                        fieldName: 'total_home_financing_limit',
                        value: floatValue,
                      })
                    }
                  />
                </label>

                {/* ACL */}
                <label className='form-control'>
                  <div className='label'>
                    <span className='label-text'>Annualized Cost Limit (ACL)</span>
                  </div>
                  <Field
                    name='annualized_cost_limit'
                    {...defaultCurrencyInputProps}
                    placeholder='%'
                    suffix='%'
                    onValueChange={({ floatValue }) =>
                      setNumericValue({
                        setFieldValue,
                        fieldName: 'annualized_cost_limit',
                        value: floatValue,
                      })
                    }
                  />
                </label>

                <label className='form-control'>
                  <div className='label'>
                    <span className='label-text'>Combined Loan to Value (CLTV)</span>
                  </div>
                  <Field
                    name='cltv_limit'
                    {...defaultCurrencyInputProps}
                    placeholder='%'
                    suffix='%'
                    prefix=''
                    onValueChange={({ floatValue }) =>
                      setNumericValue({
                        setFieldValue,
                        fieldName: 'cltv_limit',
                        value: floatValue,
                      })
                    }
                  />
                </label>

                {/* Exchange Rate Adjustment */}
                <label className='form-control'>
                  <div className='label'>
                    <span className='label-text'>
                      {useERA ? 'Exchange Rate Adjustment' : 'Exchange Rate (Current)'}
                    </span>
                    <span className='label-text-alt'>
                      {overrides?.exchange_rate_adjustment_type && (
                        <span className='badge bg-success/50 lowercase'>
                          {overrides?.exchange_rate_adjustment_type}
                        </span>
                      )}
                    </span>
                  </div>
                  {useERA ? (
                    <Field
                      as='select'
                      name='exchange_rate_adjustment'
                      className='select select-bordered select-sm disabled:font-semibold leading-none disabled:text-black'
                      disabled={!allowOverrides}
                    >
                      {adjustmentOptions.map(({ label, value }) => (
                        <option
                          key={value}
                          value={value}
                        >
                          {label}
                        </option>
                      ))}
                    </Field>
                  ) : (
                    <Field
                      name='exchange_rate_adjustment'
                      className={defaultInputClasses}
                    />
                  )}
                </label>

                {/* Term */}
                <label className='form-control'>
                  <div className='label'>
                    <span className='label-text'>Term</span>
                  </div>
                  <Field
                    name='term'
                    {...defaultNumericInputProps}
                    placeholder='Years'
                    onValueChange={({ floatValue }) =>
                      setNumericValue({
                        setFieldValue,
                        fieldName: 'term',
                        value: floatValue,
                      })
                    }
                  />
                </label>

                {/* Exchange Rate */}
                <label className='form-control'>
                  <div className='label'>
                    <span className='label-text'>
                      {useERA ? 'Exchange Rate' : 'Exchange Rate (Max)'}
                    </span>
                  </div>
                  {useERA ? (
                    <input // Intentionally not a Field
                      readOnly
                      className={defaultInputClasses}
                      value={
                        baseExchangeRate
                          ? `${rateFormat.format(
                              applicationOffer?.selectedOffer?.offers?.exchangeRate
                            )}X`
                          : 'Not set'
                      }
                    />
                  ) : (
                    <Field
                      name='exchange_rate_max_offer'
                      {...defaultCurrencyInputProps}
                      placeholder='Rate'
                      readOnly={!allowOverrides}
                      onValueChange={({ floatValue }) =>
                        setNumericValue({
                          setFieldValue,
                          fieldName: 'exchange_rate_max_offer',
                          value: floatValue,
                        })
                      }
                    />
                  )}
                  <div className='label'>
                    <span className='label-text-alt'>
                      <span className={exceedLimit ? 'text-error' : 'text-info'}>
                        {`Base ER: ${
                          baseExchangeRate ? baseExchangeRate : '-'
                        } (ER limit is ${ERLimit}X)`}
                      </span>
                    </span>
                  </div>
                </label>

                {/* DSCR */}
                <label className='form-control'>
                  <div className='label'>
                    <span className='label-text'>DSCR</span>
                    <span className='label-text-alt'>
                      {<AlertFlag flag='missing-data-dscr-on-rental-property' />}
                    </span>
                  </div>
                  {isInvestmentProperty ? (
                    <label className='input input-sm input-bordered font-semibold disabled:text-gray-800'>
                      <DSCRCalculatorField
                        applicationUUUID={application?.uuid}
                        applicationId={application?.id}
                        overridesValue={values.dscr}
                        onBlur={submitForm} // Still uses legacy onBlur :sigh:
                        readOnly={!dscrEditAllowed(loggedUser)}
                      />
                    </label>
                  ) : (
                    <Field
                      name='dscr'
                      {...defaultCurrencyInputProps}
                      placeholder='e.g 1X'
                      suffix='X'
                      onValueChange={({ value }) =>
                        setNumericValue({
                          setFieldValue,
                          fieldName: 'dscr',
                          value,
                        })
                      }
                      decimalScale={2}
                      readOnly={!allowOverrides}
                    />
                  )}
                </label>

                {/* Min Investment */}
                <label className='form-control'>
                  <div className='label'>
                    <span className='label-text'>Min Investment</span>
                  </div>
                  <Field
                    name='min_investment'
                    {...defaultCurrencyInputProps}
                    placeholder='$'
                    prefix='$'
                    thousandSeparator
                    onValueChange={({ floatValue }) =>
                      setNumericValue({
                        setFieldValue,
                        fieldName: 'min_investment',
                        value: floatValue,
                      })
                    }
                  />
                </label>

                {/* Max Investment */}
                <label className='form-control'>
                  <div className='label'>
                    <span className='label-text'>Max Investment</span>
                  </div>
                  <Field
                    name='max_investment'
                    {...defaultCurrencyInputProps}
                    placeholder='$'
                    prefix='$'
                    thousandSeparator
                    onValueChange={({ floatValue }) =>
                      setNumericValue({
                        setFieldValue,
                        fieldName: 'max_investment',
                        value: floatValue,
                      })
                    }
                  />
                </label>

                {/* Credit Score / FICO */}
                <label className='form-control'>
                  <div className='label'>
                    <span className='label-text'>Credit Score</span>
                  </div>
                  <Field
                    name='fico'
                    placeholder='FICO'
                    {...defaultCurrencyInputProps}
                    prefix=''
                    onValueChange={({ floatValue }) =>
                      setNumericValue({
                        setFieldValue,
                        fieldName: 'fico',
                        value: floatValue,
                      })
                    }
                  />
                </label>

                {/* Pricing Version */}
                <label className='form-control'>
                  <div className='label'>
                    <span className='label-text'>Pricing Version</span>
                  </div>
                  <Field
                    name='pricing_version'
                    className={defaultInputClasses}
                    readOnly
                  />
                </label>

                {/* Home Value */}
                <label className='form-control'>
                  <div className='label'>
                    <span className='label-text'>Home Value</span>
                  </div>
                  <Field
                    name='home_value'
                    {...defaultCurrencyInputProps}
                    placeholder='$0'
                    decimalScale={0}
                    prefix='$'
                    thousandSeparator
                    onValueChange={({ floatValue }) =>
                      setNumericValue({
                        setFieldValue,
                        fieldName: 'home_value',
                        value: floatValue,
                      })
                    }
                  />
                </label>

                {!shouldHideDTI && (
                  <label className='form-control'>
                    <div className='label'>
                      <span className='label-text'>DTI</span>
                    </div>
                    <Field
                      name='dti'
                      {...defaultCurrencyInputProps}
                      readOnly={!allowOverrides}
                      suffix='%'
                      onValueChange={({ floatValue }) =>
                        setNumericValue({
                          setFieldValue,
                          fieldName: 'dti',
                          value: floatValue,
                        })
                      }
                    />
                  </label>
                )}

                <div className='flex gap-x-5 col-span-2 mt-3 justify-end'>
                  <button
                    type='submit'
                    className='btn btn-primary'
                    disabled={!dirty}
                  >
                    Save
                  </button>
                </div>
              </fieldset>
            </Form>
          )}
        </Formik>
      </div>
    </section>
  )
}
